import cloneDeep from 'lodash/cloneDeep'

import { images } from '~/constants/data-reports/images'
import { SUSTAINABILITY_CARD_IMAGES } from '~/constants/data-reports/sustainability-card-images'
import { forecastsImagesCard } from '~/constants/forecasts'
import { MARKET_TYPE } from '~/src/constants/markets'
import type { TObjectValues } from '~/src/types/generics'
import type { TReport, TReportCard } from '~/src/types/reports'

import type { TMarketSlug } from '~/src/types/markets'
import type { groupReportsByYear } from '~/src/utils/reports/filter'

export type TReportFileType = 'video' | 'pdf' | 'excel'

export const getReportFileType = (report: TReport): TReportFileType => {
  const slugs = (report.tags || []).map((tag) => tag.slug)
  const isVideo = slugs.some((slug) => VIDEO_TAGS.includes(slug))
  const isPdf = report.file_name?.includes('pdf')

  if (isVideo) return 'video'
  if (isPdf) return 'pdf'
  return 'excel'
}

const getGenericGenerator = (imageSet: string[]) => {
  let imageCache: string[] = []

  const next = () => {
    if (!imageCache || imageCache.length === 0) {
      imageCache = cloneDeep(imageSet)
    }

    const currentImages = imageCache
    const image = currentImages.shift()
    if (image) {
      currentImages.push(image)
    }
    return image
  }

  const reset = (startAt: number = 0) => {
    imageCache = cloneDeep(imageSet)

    if (startAt > 0) {
      imageCache = imageCache.slice(startAt)
    } else {
      imageCache = []
    }
  }

  return { next, reset }
}

const VIDEO_TAGS = ['forecast-video', 'forecast-video-intro']

type TForecastMarketSlugs = keyof typeof forecastsImagesCard
type TForecastMarketImages = TObjectValues<typeof forecastsImagesCard>

const getBaseImagesForForecastReports = (category: TForecastMarketSlugs) => {
  if (!forecastsImagesCard[category]) {
    throw new Error(`No images found for category: ${category}`)
  }
  return JSON.parse(
    JSON.stringify(forecastsImagesCard[category]),
  ) as TForecastMarketImages
}

const getForecastGenerator = () => {
  let _images: Partial<typeof forecastsImagesCard> = {}

  const next = (report: TReport, marketSlug: TForecastMarketSlugs) => {
    if (!_images[marketSlug]) {
      _images[marketSlug] = getBaseImagesForForecastReports(marketSlug)
    }

    const fileType = getReportFileType(report)

    if (fileType === 'video') {
      const image = _images[marketSlug].VIDEO.shift()
      _images[marketSlug].VIDEO.push(image)
      return image
    }

    if (fileType === 'pdf') {
      const image = _images[marketSlug].PDF.shift()
      _images[marketSlug].PDF.push(image)
      return image
    }

    const image = _images[marketSlug].EXCEL.shift()
    _images[marketSlug].EXCEL.push(image)
    return image
  }

  const reset = () => {
    _images = {}
  }

  return { next, reset }
}

export const ReportImageGenerator = {
  price: getGenericGenerator(images),
  forecast: getForecastGenerator(),
  sustainability: getGenericGenerator(SUSTAINABILITY_CARD_IMAGES),
  default: getGenericGenerator(images),
}

export function setReportImages(
  reports: TReport[],
  marketSlug: TMarketSlug,
  startAt = 0,
): TReportCard[] {
  const generator = ReportImageGenerator

  generator.price.reset(startAt)
  generator.forecast.reset()
  generator.sustainability.reset(startAt)
  generator.default.reset(startAt)

  return reports.map((report) => {
    let image = ''

    if (report.market_type === MARKET_TYPE.PRICES_ASSESSMENTS) {
      image = generator.price.next()
    }

    if (report.market_type === MARKET_TYPE.FORECASTS) {
      image = generator.forecast.next(report, marketSlug)
    }

    if (report.market_type === MARKET_TYPE.SUSTAINABILITY) {
      image = generator.sustainability.next()
    }

    if (!image) {
      image = generator.default.next()
    }

    report.images = {
      desktop: image,
    }

    return report
  })
}

export function setReportGroupImages(
  groupedReports: ReturnType<typeof groupReportsByYear>,
  marketSlug: TMarketSlug,
  startAt = 0,
) {
  const ungroupedReports = []
  const sortedYears = Object.keys(groupedReports).sort((a, b) => {
    const yearA = parseInt(a)
    const yearB = parseInt(b)
    return yearB - yearA
  })

  for (const year of sortedYears) {
    ungroupedReports.push(...groupedReports[year])
  }
  setReportImages(ungroupedReports, marketSlug, startAt)
}
